import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiSearch,
  FiX,
  FiAlertCircle,
  FiMapPin,
  FiChevronRight,
  FiChevronDown,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import './DestinationsPage.css';

/* ============================================================
   Breadcrumb
   ============================================================ */
const Breadcrumb = ({ items }) => (
  <nav className="dx-breadcrumb" aria-label="Breadcrumb">
    {items.map((item, i) => (
      <React.Fragment key={`${item.label}-${i}`}>
        {i > 0 && (
          <span className="dx-breadcrumb-sep" aria-hidden="true">
            /
          </span>
        )}
        {item.href ? (
          <a className="dx-breadcrumb-link" href={item.href}>
            {item.label}
          </a>
        ) : (
          <span className="dx-breadcrumb-current" aria-current="page">
            {item.label}
          </span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

/* ============================================================
   Destination Card
   ============================================================ */
const DestinationCard = ({ destination, index = 0 }) => {
  const navigate = useNavigate();

  const location =
    destination.city && destination.state
      ? `${destination.city.name}, ${destination.state.name}`
      : destination.state?.name || destination.city?.name || '';

  const packages = destination.packages_count || 0;

  const open = () =>
    navigate(`/destination-wedding-packages/${destination?.id}`);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };

  return (
    <article
      className="dx-card"
      style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
      onClick={open}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Explore ${destination.name}`}
    >
      <div className="dx-card-media">
        <img
          className="dx-card-img"
          src={destination.featured_photo_url || destination.banner_url}
          alt={destination.name}
          loading="lazy"
        />
        <span className="dx-card-veil" aria-hidden="true" />

        {packages > 0 && (
          <span className="dx-card-badge">
            {packages} Package{packages !== 1 ? 's' : ''}
          </span>
        )}

        {location && (
          <span className="dx-card-loc">
            <FiMapPin aria-hidden="true" />
            {location}
          </span>
        )}
      </div>

      <div className="dx-card-body">
        <h3 className="dx-card-title">{destination.name}</h3>
        <p className="dx-card-desc">
          {destination.short_description || 'No description available'}
        </p>

        <span className="dx-card-cta">
          Explore Venue
          <FiChevronRight aria-hidden="true" />
        </span>
      </div>
    </article>
  );
};

/* ============================================================
   Filter Panel  (sidebar on desktop / bottom-sheet on mobile)
   ============================================================ */
const FilterPanel = ({
  filters,
  onFilterChange,
  onClear,
  onClose,
  states,
  cities,
  resultCount,
  activeCount,
}) => (
  <div className="dx-panel">
    <span className="dx-panel-grip" aria-hidden="true" />

    <div className="dx-panel-head">
      <div className="dx-panel-head-text">
        <h3 className="dx-panel-title">Filters</h3>
        <p className="dx-panel-hint">
          {activeCount > 0
            ? `${activeCount} filter${activeCount > 1 ? 's' : ''} active`
            : 'Refine your search'}
        </p>
      </div>

      <button
        type="button"
        className="dx-panel-close"
        onClick={onClose}
        aria-label="Close filters"
      >
        <FiX />
      </button>
    </div>

    <div className="dx-field">
      <label className="dx-label" htmlFor="dx-state">
        State
      </label>
      <div className="dx-select-wrap">
        <select
          id="dx-state"
          className="dx-select"
          value={filters.state || ''}
          onChange={(e) => onFilterChange({ state: e.target.value, city: '' })}
        >
          <option value="">All States</option>
          {states.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
        <FiChevronDown className="dx-select-caret" aria-hidden="true" />
      </div>
    </div>

    <div className="dx-field">
      <label className="dx-label" htmlFor="dx-city">
        City
      </label>
      <div className="dx-select-wrap">
        <select
          id="dx-city"
          className="dx-select"
          value={filters.city || ''}
          onChange={(e) => onFilterChange({ city: e.target.value })}
          disabled={cities.length === 0}
        >
          <option value="">All Cities</option>
          {cities.map((city) => (
            <option key={city} value={city}>
              {city}
            </option>
          ))}
        </select>
        <FiChevronDown className="dx-select-caret" aria-hidden="true" />
      </div>
    </div>

    <div className="dx-panel-actions">
      <button
        type="button"
        className="dx-btn-ghost"
        onClick={onClear}
        disabled={activeCount === 0}
      >
        Clear all filters
      </button>

      <button type="button" className="dx-btn-apply" onClick={onClose}>
        Show {resultCount} result{resultCount !== 1 ? 's' : ''}
      </button>
    </div>
  </div>
);

/* ============================================================
   Results list
   ============================================================ */
const DestinationList = ({ destinations, loading }) => {
  if (loading) {
    return (
      <div className="dx-grid" aria-busy="true" aria-label="Loading destinations">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="dx-skeleton" />
        ))}
      </div>
    );
  }

  if (!destinations.length) {
    return (
      <div className="dx-empty">
        <span className="dx-empty-icon">
          <FiAlertCircle />
        </span>
        <h3 className="dx-empty-title">No Destinations Found</h3>
        <p className="dx-empty-text">
          Try adjusting your filters or search terms
        </p>
      </div>
    );
  }

  return (
    <div className="dx-grid">
      {destinations.map((dest, i) => (
        <DestinationCard key={dest.id} destination={dest} index={i} />
      ))}
    </div>
  );
};

/* ============================================================
   Main Page
   ============================================================ */
export default function DestinationsPage() {
  const [filters, setFilters] = useState({ state: '', city: '', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [allDestinations, setAllDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [states, setStates] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  /* ---------- Fetch destinations ---------- */
  useEffect(() => {
    let alive = true;

    const fetchDestinations = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          'https://admin.jayrajgroup.com/api/wedding/destinations'
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        if (!result.status || !result.data) {
          throw new Error(result.message || 'Failed to fetch data');
        }
        if (!alive) return;

        setAllDestinations(result.data);
        setStates(
          [...new Set(result.data.map((d) => d.state?.name).filter(Boolean))].sort()
        );
      } catch (err) {
        if (!alive) return;
        setError(err.message);
        setAllDestinations([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchDestinations();
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  /* ---------- City options (depends on selected state) ---------- */
  const cities = useMemo(() => {
    const pool = filters.state
      ? allDestinations.filter((d) => d.state?.name === filters.state)
      : allDestinations;

    return [...new Set(pool.map((d) => d.city?.name).filter(Boolean))].sort();
  }, [allDestinations, filters.state]);

  /* ---------- Keep city valid when state changes ---------- */
  useEffect(() => {
    if (filters.city && !cities.includes(filters.city)) {
      setFilters((prev) => ({ ...prev, city: '' }));
    }
  }, [cities, filters.city]);

  /* ---------- Debounced live search ---------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput }
      );
    }, 320);
    return () => clearTimeout(timer);
  }, [searchInput]);

  /* ---------- Client-side filtering ---------- */
  const filteredDestinations = useMemo(() => {
    let results = [...allDestinations];

    if (filters.state) {
      results = results.filter((d) => d.state?.name === filters.state);
    }

    if (filters.city) {
      results = results.filter((d) => d.city?.name === filters.city);
    }

    if (filters.search) {
      const q = filters.search.toLowerCase().trim();
      results = results.filter(
        (d) =>
          d.name?.toLowerCase().includes(q) ||
          d.city?.name?.toLowerCase().includes(q) ||
          d.state?.name?.toLowerCase().includes(q) ||
          d.short_description?.toLowerCase().includes(q)
      );
    }

    return results;
  }, [allDestinations, filters]);

  /* ---------- Derived stats ---------- */
  const totalCities = useMemo(
    () => new Set(allDestinations.map((d) => d.city?.name).filter(Boolean)).size,
    [allDestinations]
  );

  const activeCount =
    (filters.state ? 1 : 0) + (filters.city ? 1 : 0) + (filters.search ? 1 : 0);

  /* ---------- Body scroll lock for the mobile sheet ---------- */
  useEffect(() => {
    if (!sheetOpen || window.innerWidth >= 1024) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setSheetOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [sheetOpen]);

  /* ---------- Handlers ---------- */
  const updateFilter = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({ state: '', city: '', search: '' });
    setSearchInput('');
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput }));
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setFilters((prev) => ({ ...prev, search: '' }));
  };

  /* ---------- Error state ---------- */
  if (error) {
    return (
      <div className="dx-page">
        <span className="dx-orb dx-orb-a" aria-hidden="true" />
        <span className="dx-orb dx-orb-b" aria-hidden="true" />

        <div className="dx-shell">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/destination-wedding' },
              { label: 'Destinations' },
            ]}
          />
          <div className="dx-empty dx-empty-error">
            <span className="dx-empty-icon">
              <FiAlertCircle />
            </span>
            <h3 className="dx-empty-title">Error loading destinations</h3>
            <p className="dx-empty-text">{error}</p>
            <button
              type="button"
              className="dx-btn-search dx-btn-retry"
              onClick={() => setReloadKey((k) => k + 1)}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Render ---------- */
  return (
    <div className="dx-page">
      <span className="dx-orb dx-orb-a" aria-hidden="true" />
      <span className="dx-orb dx-orb-b" aria-hidden="true" />

      <div className="dx-shell">
        <Breadcrumb
          items={[
            { label: 'Home', href: '/destination-wedding' },
            { label: 'Destinations' },
          ]}
        />

        {/* ---------- Hero ---------- */}
        <header className="dx-hero">
          <span className="dx-eyebrow">Curated Wedding Destinations</span>
          <h1 className="dx-title">Explore Our Destinations</h1>
          <p className="dx-subtitle">
            Discover the perfect destination for your wedding or travel experience
          </p>

          <div className="dx-stats">
            <div className="dx-stat">
              <span className="dx-stat-value">{allDestinations.length}</span>
              <span className="dx-stat-label">Destinations</span>
            </div>
            <div className="dx-stat">
              <span className="dx-stat-value">{states.length}</span>
              <span className="dx-stat-label">States</span>
            </div>
            <div className="dx-stat">
              <span className="dx-stat-value">{totalCities}</span>
              <span className="dx-stat-label">Cities</span>
            </div>
          </div>
        </header>

        {/* ---------- Toolbar ---------- */}
        <div className="dx-toolbar">
          <form className="dx-toolbar-inner" onSubmit={handleSearchSubmit}>
            <div className="dx-search">
              <FiSearch className="dx-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="dx-search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search destinations, states or cities"
                aria-label="Search destinations"
              />
              {searchInput && (
                <button
                  type="button"
                  className="dx-search-clear"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <FiX />
                </button>
              )}
            </div>

            <button type="submit" className="dx-btn-search">
              <FiSearch className="dx-btn-search-icon" aria-hidden="true" />
              <span>Search</span>
            </button>

            <button
              type="button"
              className="dx-btn-filter"
              onClick={() => setSheetOpen((s) => !s)}
              aria-label={sheetOpen ? 'Close filters' : 'Open filters'}
              aria-expanded={sheetOpen}
              aria-controls="dx-filters"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="7" y1="12" x2="17" y2="12" />
                <line x1="10" y1="18" x2="14" y2="18" />
              </svg>
              {activeCount > 0 && (
                <span className="dx-filter-count">{activeCount}</span>
              )}
            </button>
          </form>
        </div>

        {/* ---------- Quick state chips ---------- */}
        {states.length > 0 && (
          <div className="dx-chips" role="tablist" aria-label="Quick state filters">
            <button
              type="button"
              role="tab"
              aria-selected={!filters.state}
              className={`dx-chip ${!filters.state ? 'is-active' : ''}`}
              onClick={() => updateFilter({ state: '', city: '' })}
            >
              All
            </button>
            {states.map((state) => (
              <button
                key={state}
                type="button"
                role="tab"
                aria-selected={filters.state === state}
                className={`dx-chip ${filters.state === state ? 'is-active' : ''}`}
                onClick={() =>
                  updateFilter({
                    state: filters.state === state ? '' : state,
                    city: '',
                  })
                }
              >
                {state}
              </button>
            ))}
          </div>
        )}

        {/* ---------- Layout ---------- */}
        <div className="dx-layout">
          <button
            type="button"
            className={`dx-backdrop ${sheetOpen ? 'is-open' : ''}`}
            onClick={() => setSheetOpen(false)}
            aria-label="Close filters"
            tabIndex={sheetOpen ? 0 : -1}
          />

          <aside
            id="dx-filters"
            className={`dx-sidebar ${sheetOpen ? 'is-open' : ''}`}
            role={sheetOpen ? 'dialog' : undefined}
            aria-modal={sheetOpen ? 'true' : undefined}
            aria-label="Destination filters"
          >
            <FilterPanel
              filters={filters}
              onFilterChange={updateFilter}
              onClear={handleClearFilters}
              onClose={() => setSheetOpen(false)}
              states={states}
              cities={cities}
              resultCount={filteredDestinations.length}
              activeCount={activeCount}
            />
          </aside>

          <main className="dx-results">
            <div className="dx-results-head">
              <p className="dx-results-count">
                {loading ? (
                  'Finding destinations…'
                ) : (
                  <>
                    <strong>{filteredDestinations.length}</strong>{' '}
                    {filteredDestinations.length === 1
                      ? 'destination'
                      : 'destinations'}{' '}
                    found
                  </>
                )}
              </p>

              {activeCount > 0 && !loading && (
                <button
                  type="button"
                  className="dx-results-reset"
                  onClick={handleClearFilters}
                >
                  Reset
                </button>
              )}
            </div>

            <DestinationList
              destinations={filteredDestinations}
              loading={loading}
            />
          </main>
        </div>
      </div>
    </div>
  );
}